import type Excel from 'exceljs';
import { saveAs } from 'file-saver';
import { difference } from 'lodash/fp';
import { applyCellStyle, headCellStyle, type ExcelFile, getWorkbook } from './excel';

// prepare pure workbook for excel
export const prepareWorkbook = async () => {
    const Excel = await import('exceljs').then(imported => imported.default);

    const workbook = new Excel.Workbook();

    workbook.addWorksheet();

    return workbook;
};

const makeAsteriskRed = (cell: Excel.Cell) => {
    const richText: any[] = cell.value
        .toString()
        .split('*')
        .map(i => ({ text: i, font: { name: 'Arial' } }));

    for (let i = 1; i < richText.length; i += 2) {
        richText.splice(i, 0, { font: { color: { argb: 'FFFF0000', name: 'Arial' } }, text: '*' });
    }

    // eslint-disable-next-line no-param-reassign
    cell.value = {
        richText,
    };
};

// prepare first row for matrix lease reference table
export const prepareFirstRow = (worksheet, columns = [], headerData = []) => {
    if (columns.length < 2) {
        throw new Error('Excel columns are not supported!');
    }

    const firstRow = worksheet.getRow(1);

    // other columns except matrix column
    columns.forEach((column, index) => {
        if (index < columns.length - 2) {
            const cell = firstRow.getCell(index + 1);
            cell.value = column;
            cell.fill = headCellStyle;
            makeAsteriskRed(cell);
        }
    });

    // matrix columns
    const specialCell = worksheet.getRow(1).getCell(columns.length - 1);
    specialCell.border = {
        diagonal: { up: false, down: true, style: 'dotted' },
    };
    specialCell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: true,
    };
    specialCell.fill = headCellStyle;
    specialCell.value = `                                  ${columns[columns.length - 1]} \n ${
        columns[columns.length - 2]
    }`;
    makeAsteriskRed(specialCell);

    // data columns
    headerData.forEach((column, index) => {
        firstRow.getCell(index + columns.length).value = column;
    });

    firstRow.height = 35;
};

const applyHeaderStyle = (worksheet: Excel.Worksheet, headerRowNumber: number = 1) => {
    const headerRow = worksheet.getRow(headerRowNumber);

    for (let i = 1; i <= headerRow.cellCount; i++) {
        const column = worksheet.getColumn(i);
        column.style = { font: { name: 'Arial' } };
        column.width = 30;
    }
};

export const exportFinanceProductTableExcel = (
    workbook: Excel.Workbook,
    productName: string,
    tableName: string,
    headerRowNumber: number = 1
) => {
    const worksheet = workbook.getWorksheet(1);

    applyHeaderStyle(worksheet, headerRowNumber);
    applyCellStyle(worksheet);

    workbook.xlsx.writeBuffer().then(data => {
        const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `${productName} - ${tableName}.xlsx`);
    });
};

const getMissingHeaders = (sheet: Excel.Worksheet, mappedHeaders: { key: string; text: string }[]) => {
    const row = sheet.getRow(1);
    const requiredHeaders = mappedHeaders.map(i => i.text);

    if (requiredHeaders.length < 2) {
        return [];
    }

    const texts = requiredHeaders.map((_i, index) => row.getCell(index + 1).text);

    return difference(
        requiredHeaders,
        requiredHeaders.filter(
            requiredHeader => texts.includes(requiredHeader) || texts[texts.length - 2].includes(requiredHeader)
        )
    );
};

const getValue = text => (!Number.isNaN(parseFloat(text)) && !Number.isNaN(Number(text)) ? +text : text);

const getData = (sheet: Excel.Worksheet, mappedHeaders: { key: string; text: string }[]) => {
    const data = [];
    const errors: string[] = [];

    // skip empty row
    sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        const missingTermCol: number[] = [];
        const missingRequiredFields: string[] = [];
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            // break column number
            const breakColumnNumber = mappedHeaders.length - 1;
            // skip title
            if (rowNumber === 1 && colNumber > breakColumnNumber && cell.value === null) {
                // check if miss header data
                missingTermCol.push(colNumber);
            } else if (colNumber <= breakColumnNumber && rowNumber > 1 && cell.value === null) {
                // check if miss required columns
                const headerTitle =
                    mappedHeaders[colNumber <= breakColumnNumber ? colNumber - 1 : breakColumnNumber].text;

                const isRequired = headerTitle?.includes('*');
                if (isRequired) {
                    missingRequiredFields.push(headerTitle);
                }
            } else if (rowNumber > 1 && colNumber > breakColumnNumber && cell.value !== null) {
                // fetch data
                const otherProperty = mappedHeaders.reduce(
                    (acc, item, index) =>
                        index < breakColumnNumber
                            ? {
                                  ...acc,
                                  [item.key]: getValue(row.getCell(index + 1).text),
                              }
                            : acc,
                    {}
                );

                const item = {
                    [mappedHeaders[breakColumnNumber].key]: getValue(sheet.getRow(1).getCell(colNumber).text),
                    value: getValue(cell.text),
                    ...otherProperty,
                };
                data.push(item);
            }
        });

        if (missingTermCol.length > 0) {
            errors.push(`Empty Required Field/s at row #${rowNumber}: ${missingTermCol.join(', ')}`);
        }

        if (missingRequiredFields?.length > 0) {
            errors.push(`Empty Required Field/s at row #${rowNumber}: ${missingRequiredFields.join(', ')}`);
        }
    });

    return { data, errors };
};

/**
 * mappedHeaders:
 * the last item is the key and title of row data for matrix excel
 * the penultimate item is the key and title of column data for matrix excel
 */
export type getDataFromMatrixExcelProps = {
    excel: ExcelFile;
    mappedHeaders: { key: string; text: string }[];
};

export const getDataFromMatrixExcel = async ({ excel, mappedHeaders }: getDataFromMatrixExcelProps) => {
    const workbook = await getWorkbook(excel);
    const worksheet = workbook.getWorksheet(1);

    const errors: string[] = [];

    const missingHeaders = getMissingHeaders(worksheet, mappedHeaders);

    if (missingHeaders.length > 0) {
        errors.push(`Missing Header/s: ${missingHeaders.join(', ')}. Please use template and re-upload.`);
    }

    if (errors.length > 0) {
        return { errors, data: null };
    }

    const { errors: err, data } = getData(worksheet, mappedHeaders);

    if (!data?.length) {
        errors.push(`Thers's no data to import. Please fill in data and re-upload.`);

        return { errors, data: null };
    }

    return { errors: err, data };
};
